import { useState, useEffect } from "react";
import { Order } from "../services";
import { EXPEDITOR_ALL_STATUSES } from "../mock";
import moment from "moment";
import { withTenderStatusFilter } from "../utils/orderTenderFilter";

export const useOrderFilter = (currentLanguage, initialValues = {}) => {
  const [countryFrom, setCountryFrom] = useState(() => initialValues.countryFrom ?? "");
  const [countryTo, setCountryTo] = useState(() => initialValues.countryTo ?? "");
  const [cityFrom, setCityFrom] = useState(() => initialValues.cityFrom ?? "");
  const [cityTo, setCityTo] = useState(() => initialValues.cityTo ?? "");
  const [loadingDate, setLoadingDate] = useState(() => initialValues.loadingDate ?? "");
  const [creationDate, setCreationDate] = useState(() => initialValues.creationDate ?? "");
  const [selectedStatus, setSelectedStatus] = useState(() => initialValues.selectedStatus ?? []);
  const [selectedCurrency, setSelectedCurrency] = useState(() => initialValues.selectedCurrency ?? "");
  const [senderName, setSenderName] = useState(() => initialValues.senderName ?? "");
  const [orderNo, setOrderNo] = useState(() => initialValues.orderNo ?? "");
  const [receiverName, setReceiverName] = useState(() => initialValues.receiverName ?? "");
  const [executor, setExecutor] = useState(() => initialValues.executor ?? "");
  const [selectedEmployee, setSelectedEmployee] = useState(() => initialValues.selectedEmployee ?? "");

  const [countries, setCountries] = useState([]);

  const [isFilterOpen, setFilterOpen] = useState(false);

  useEffect(() => {
    Order.getCountries(currentLanguage)
      .then((res) => res.data)
      .then((data) => {
        const sorted = data?.sort((a, b) => a.name.localeCompare(b.name));
        setCountries(sorted);
      })
      .catch((error) => {
        console.error("Error getting countries:", error);
      });
  }, [currentLanguage]);

  const resetFilter = () => {
    setCountryFrom("");
    setCountryTo("");
    setCityFrom("");
    setCityTo("");
    setLoadingDate("");
    setCreationDate([]);
    setSelectedStatus([]);
    setSelectedCurrency("");
    setSenderName("");
    setOrderNo("");
    setReceiverName("");
    setExecutor("");
    setSelectedEmployee("");
  };

  const getFilterParams = ({
    page,
    limit,
    statusActive,
    sorting,
    selectedIds = [],
    unselectedIds = [],
    isExpeditor = false,
  }) => {
    const hasLoadingDateFilter = Array.isArray(loadingDate) && Boolean(loadingDate[0]);

    const params = {
      page,
      limit,
      statuses: [],
      currencies: selectedCurrency ? [selectedCurrency.value] : [],
      orderNo: String(orderNo || ""),
      countryIdFrom: countryFrom ? countryFrom.id : "",
      cityIdFrom: cityFrom ? cityFrom.id : "",
      countryIdTo: countryTo ? countryTo.id : "",
      cityIdTo: cityTo ? cityTo.id : "",
      startShippingDate:
        Array.isArray(loadingDate) && loadingDate[0]
          ? moment(loadingDate[0]).format("YYYY-MM-DD")
          : "",
      endShippingDate:
        Array.isArray(loadingDate) && loadingDate[1]
          ? moment(loadingDate[1]).format("YYYY-MM-DD")
          : "",
      startCreatedAt:
        Array.isArray(creationDate) && creationDate[0]
          ? moment(creationDate[0]).format("YYYY-MM-DD")
          : "",
      endCreatedAt:
        Array.isArray(creationDate) && creationDate[1]
          ? moment(creationDate[1]).format("YYYY-MM-DD")
          : "",
      senderName: senderName || "",
      recipientName: receiverName || "",
      userId: selectedEmployee ? selectedEmployee.id : "",
      ...(isExpeditor
        ? { senderCompanyName: executor ? executor.companyName : "" }
        : { executorName: executor ? executor.companyName : "" }),
      sort: "",
      lang: currentLanguage,
      selectedIds: selectedIds,
      unselectedIds: unselectedIds,
    };

    if (statusActive) {
      if (statusActive.key === "all") {
        params.statuses = Array.isArray(selectedStatus) && selectedStatus.length > 0
          ? selectedStatus.map((s) => s.value)
          : isExpeditor ? EXPEDITOR_ALL_STATUSES : [];
      } else {
        params.statuses = statusActive.statuses;
      }

    }

    if (hasLoadingDateFilter) {
      params.sort = "shippingDate,asc";
    } else if (sorting) {
      for (const sortingKey in sorting) {
        params.sort = `${sortingKey},${sorting[sortingKey]}`;
      }
    } else {
      params.sort = "createdAt,desc";
    }

    const requestParams = withTenderStatusFilter(params);

    if (statusActive?.key !== "all") {
      delete requestParams.isExpiredTender;

      if (typeof statusActive?.isExpiredTender === "boolean") {
        requestParams.isExpiredTender = statusActive.isExpiredTender;
      }
    }

    return requestParams;
  };

  const filterValues = {
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    orderNo,
    receiverName,
    executor,
    selectedEmployee,
  };

  const filterSetters = {
    setCountryFrom,
    setCountryTo,
    setCityFrom,
    setCityTo,
    setLoadingDate,
    setCreationDate,
    setSelectedStatus,
    setSelectedCurrency,
    setSenderName,
    setOrderNo,
    setReceiverName,
    setExecutor,
    setSelectedEmployee,
  };

  const filterMeta = {
    countries,
  };

  const filterDeps = [
    countryFrom,
    countryTo,
    cityFrom,
    cityTo,
    loadingDate,
    creationDate,
    selectedStatus,
    selectedCurrency,
    senderName,
    receiverName,
    orderNo,
    executor,
    selectedEmployee,
  ];

  return {
    filterValues,
    filterSetters,
    filterMeta,
    filterDeps,
    isFilterOpen,
    setFilterOpen,
    resetFilter,
    getFilterParams,
  };
};
